/**
 * SAT Chat - conversational assistant on the results page.
 *
 * The endpoint is stateless: session_id and summary are absent on the first
 * request, returned in the response, and must be echoed on every request after
 * that. Both are kept in sessionStorage so the thread survives a reload, along
 * with the rendered turns themselves so the page can replay them.
 */
(function (global) {
  "use strict";

  // The key itself lives in SATState.KEYS so clearAll() covers this thread
  // too; the literal is the fallback if sat-state.js has not loaded.
  var STORE_KEY = (global.SATState && global.SATState.KEYS && global.SATState.KEYS.chatThread) || "satChatThread";

  function loadThread() {
    try {
      return JSON.parse(global.sessionStorage.getItem(STORE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function saveThread(thread) {
    try {
      global.sessionStorage.setItem(STORE_KEY, JSON.stringify(thread));
    } catch (e) {}
  }

  function SATChatClient(opts) {
    this.apiBaseUrl = opts.apiBaseUrl;
    this.customerId = opts.customerId || null;
    this.assessment = opts.assessment || null;
    this.thread = loadThread();
    if (!Array.isArray(this.thread.messages)) this.thread.messages = [];
  }

  /** The turns rendered so far, oldest first. */
  SATChatClient.prototype.history = function () {
    return this.thread.messages.slice();
  };

  /** Remember one rendered turn. Transient states are never recorded. */
  SATChatClient.prototype.record = function (entry) {
    if (!entry.ts) entry.ts = Date.now();
    this.thread.messages.push(entry);
    saveThread(this.thread);
  };

  SATChatClient.prototype.headers = function () {
    var headers = {
      "Content-Type": "application/json",
      "x-source": "web-app",
      "x-platform": "web",
    };

    // x-device-id is required by this endpoint.
    if (typeof global.getDeviceId === "function") {
      headers["x-device-id"] = global.getDeviceId();
    }
    if (this.customerId) {
      var id = String(this.customerId);
      headers["x-customer-id"] =
        id.indexOf("gid://") === 0 ? id : "gid://shopify/Customer/" + id;
    }
    return headers;
  };

  SATChatClient.prototype.reset = function () {
    this.thread = { messages: [] };
    saveThread(this.thread);
  };

  SATChatClient.prototype.send = async function (message) {
    var body = { message: message };

    if (this.thread.session_id) body.session_id = this.thread.session_id;
    if (this.thread.summary) body.summary = this.thread.summary;
    if (this.assessment) body.assessment = this.assessment;

    var response = await fetch(this.apiBaseUrl + "/chat-completion", {
      method: "POST",
      headers: this.headers(),
      body: JSON.stringify(body),
    });

    var json = null;
    try {
      json = await response.json();
    } catch (e) {
      json = null;
    }

    if (!response.ok) {
      var msg =
        (json &&
          (json.message ||
            (json.errors &&
              json.errors[0] &&
              json.errors[0].messages &&
              json.errors[0].messages[0]))) ||
        "Chat is unavailable right now.";
      throw new Error(msg);
    }

    var data = (json && json.data) || {};

    // Carry the returned session forward - without these the assistant loses
    // the conversation on the next message.
    if (data.session_id) this.thread.session_id = data.session_id;
    if (data.summary) this.thread.summary = data.summary;
    saveThread(this.thread);

    return {
      reply: data.reply || "",
      products: data.products || [],
    };
  };

  global.SATChat = {
    create: function (opts) {
      return new SATChatClient(opts);
    },
  };
})(window);
